// Live Activity / Dynamic Island bridge.
//
// Web no-op. Native (iOS 16.1+) calls the LiveActivityBridge Capacitor plugin
// in ios/App/LiveActivityBridge.swift.

import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export interface LiveActivityStats {
  /** Configured ping cadence in seconds (preset stoppedIntervalMs / 1000). */
  intervalSeconds?: number;
  /** Age of the most recent GPS fix in seconds at push time. */
  lastFixAgeSeconds?: number;
  /** Pings sitting in the offline outbox. Doubles as the "queued action" badge. */
  queueDepth?: number;
  /** Short human label for the active GPS preset (e.g. "Balanced"). */
  presetLabel?: string;
  /** True if the user paused tracking from the Island/Lock Screen. */
  isPaused?: boolean;
  /** Active preset key — drives the cadence switcher highlight. */
  presetKey?: string;
  /** True when the device has no usable connection (airplane mode / no signal). */
  isOffline?: boolean;
}

export interface LiveActivityCommand {
  kind: "endTrip" | "pause" | "resume" | "manualFix" | "setCadence" | string;
  args: Record<string, string>;
  ts: number;
  seq: number;
}

export interface LiveActivityPlugin {
  start(opts: { startedAt?: number } & LiveActivityStats): Promise<{ activityId: string; reused: boolean }>;
  update(opts: { isTracking: boolean } & LiveActivityStats): Promise<void>;
  end(): Promise<void>;
  endAll(): Promise<void>;
  probe(): Promise<{
    registered: boolean;
    activitiesEnabled: boolean;
    activeCount: number;
    currentId: string;
    lastError: string;
  }>;
  drainCommands(): Promise<{ commands: LiveActivityCommand[] }>;
  addListener(
    event: "liveActivityCommand",
    cb: (cmd: LiveActivityCommand) => void,
  ): Promise<PluginListenerHandle>;
}

const Native = registerPlugin<LiveActivityPlugin>("LiveActivityBridge", {
  web: {
    async start() { return { activityId: "", reused: false }; },
    async update() {},
    async end() {},
    async endAll() {},
    async probe() {
      return { registered: false, activitiesEnabled: false, activeCount: 0, currentId: "", lastError: "web" };
    },
    async drainCommands() { return { commands: [] }; },
    async addListener() {
      return { remove: async () => {} } as PluginListenerHandle;
    },
  },
});

function isIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

let availabilityLogged = false;
function logAvailabilityOnce() {
  if (availabilityLogged) return;
  availabilityLogged = true;
  if (isIOS()) {
    console.log("[live-activity] available (iOS native bridge)");
  } else {
    console.log("[live-activity] unavailable — running on", Capacitor.getPlatform(), "(no-op)");
  }
}

export async function startTrackingLiveActivity(
  startedAt: Date = new Date(),
  stats: LiveActivityStats = {},
): Promise<void> {
  logAvailabilityOnce();
  if (!isIOS()) return;
  console.log("[live-activity] start requested", { startedAt: startedAt.toISOString(), stats });
  try {
    const res = await Native.start({ startedAt: startedAt.getTime(), ...stats });
    console.log("[live-activity] start success", res);
  } catch (e) {
    console.warn("[live-activity] start failure", e);
  }
}

export async function updateTrackingLiveActivity(
  isTracking: boolean,
  stats: LiveActivityStats = {},
): Promise<void> {
  if (!isIOS()) return;
  try { await Native.update({ isTracking, ...stats }); } catch (e) { console.warn("[live-activity] update failed", e); }
}

export async function endTrackingLiveActivity(opts?: { source?: string }): Promise<void> {
  logAvailabilityOnce();
  const source = opts?.source ?? "unspecified";
  if (!isIOS()) return;
  console.log("[live-activity] end requested", { source });
  try {
    await Native.end();
    console.log("[live-activity] end success", { source });
  } catch (e) {
    console.warn("[live-activity] end failure", { source, error: e });
  }
}

/**
 * @deprecated Use `endOrphanLiveActivitiesIfSafe`. Startup, cold-start, and
 * trip-mount orphan cleanup MUST route through the guarded helper so a live
 * tracking session is never killed by boot logic. This shim now forwards to
 * the guarded variant; the old unguarded "endAll requested" log path has
 * been removed. Explicit final Stop/end goes through `endTrackingLiveActivity`.
 */
export async function endOrphanLiveActivities(opts?: { source?: string }): Promise<void> {
  return endOrphanLiveActivitiesIfSafe(opts);
}

/**
 * Orphan-clear guard. Refuses to call native `endAll` when there's any sign
 * tracking is still in use (persisted phase active/paused, or the active
 * watch handle is live). Every decision is logged with shape:
 *   [live-activity] endAll decision source=... phase=... watchRunning=... decision=preserve|clear
 */
export async function endOrphanLiveActivitiesIfSafe(opts?: { source?: string }): Promise<void> {
  const source = opts?.source ?? "unspecified";
  if (!isIOS()) {
    // Web/Android: nothing to clear, log only when verbose.
    return;
  }
  let phase: "off" | "active" | "paused" = "off";
  let watchRunning = false;
  try {
    const ts = await import("./tracking-state");
    phase = await ts.getTrackingPhase();
  } catch { /* ignore */ }
  try {
    const aw = await import("./active-watch");
    watchRunning = aw.isActiveWatchRunning() || aw.isActiveWatchPaused();
  } catch { /* ignore */ }

  if (phase !== "off" || watchRunning) {
    console.info(
      `[live-activity] endAll decision source=${source} phase=${phase} watchRunning=${watchRunning} decision=preserve`,
    );
    return;
  }
  console.info(
    `[live-activity] endAll decision source=${source} phase=${phase} watchRunning=${watchRunning} decision=clear`,
  );
  try {
    await Native.endAll();
  } catch (e) {
    console.warn("[live-activity] endAll failed", { source, error: e });
  }
}



export async function probeLiveActivity() {
  return Native.probe();
}

/** Drain any AppIntent commands queued in shared App Group storage. */
export async function drainLiveActivityCommands(): Promise<LiveActivityCommand[]> {
  if (!isIOS()) return [];
  try {
    const res = await Native.drainCommands();
    return res?.commands ?? [];
  } catch (e) {
    console.warn("[live-activity] drainCommands failed", e);
    return [];
  }
}

/** Subscribe to live AppIntent commands while the app is foregrounded. */
export async function addLiveActivityCommandListener(
  cb: (cmd: LiveActivityCommand) => void,
): Promise<PluginListenerHandle> {
  if (!isIOS()) return { remove: async () => {} } as PluginListenerHandle;
  return Native.addListener("liveActivityCommand", cb);
}
